import { omit } from 'lodash';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSession } from 'hooks';
import { useDispatch } from 'react-redux';
import { deleteBuildingRequest } from 'actions/buildingActions';
import { useTranslation } from 'react-i18next';
import { SuccessMessage, ErrorMessage } from 'ui/shared/messages';
import { CheckField, LegacySelectField, TextField, UrlField } from 'ui/shared/controls';
import { toUrlSlug } from 'shared/util/toUrlSlug';
import { countries, regionsByCountry } from 'data/countries';
import { Building, BuildingProperties } from 'shared/models';
import { Permission } from 'shared/permissions';
import './BuildingForm.css';


// NOTE: can't refer to process.env on the client if we're expecting values
//       to be injected with dotenv. All the process.env values need to be
//       baked in during the compilation via webpack & DefinePlugin
const BASE_URL = window.location.origin;

// Show Canada, USA, and Italy at the top of the list of countries
// because they are used more frequently than other countries.
const priorityCountries = [ 'CA', 'US', 'IT' ];

const sortedCountries =
    Object.keys(countries)
        .map(countryCode => ({
            countryCode,
            name: countries[countryCode],
        }))
        .sort((a, b) => {
            for (const cc of priorityCountries) {
                if (a.countryCode === cc) return -1;
                if (b.countryCode === cc) return 1;
            }
            return a.name.localeCompare(b.name);
        });


export type Props = {
    busy: boolean;
    building: Building | null;
    create?: boolean;
    errorMessage: string;
    successMessage?: string;
    onSave(details: BuildingDetails): void;
};

export type BuildingDetails = {
    id?: string;
    displayName?: string;
    streetNumber?: string;
    streetName?: string;
    city?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    urlSlug?: string;
    latitude?: string;
    longitude?: string;
    timeZone?: string;
    hasBroadcast?: boolean;
    properties: Partial<BuildingProperties>;
};

//
// Helper function to calculate the starting value of addressAvailable
// based on the input building
//
function initAddressAvailable(building: Building | null): boolean {
    const { country, streetName, streetNumber, city, region } = building || {};
    return !!(country && region && city && streetName && streetNumber);
}

//
// Helper function to calculate the starting value of the building details
// based on the input building. Note: we convert the geolocation coordinates
// into strings because the values are going in text fields -- they'll be
// converted back to numbers later on.
//
function initDetails(building: Building | null): BuildingDetails {
    return building
        ? {
            ...building,
            latitude: `${building.latitude || ''}`,
            longitude: `${building.longitude || ''}`,
        }
        : {
            properties: {},
        };
}

export const BuildingForm = ({ busy, building, create = false, errorMessage, successMessage = '', onSave }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();

    const [ addressAvailable, setAddressAvailable ] = useState(initAddressAvailable(building));
    const [ details, setDetails ] = useState<BuildingDetails>(initDetails(building));

    const isNew = !building;
    const buildingId = building ? building.id : null;

    const dispatch = useDispatch();

    
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const detailsToSubmit =
            addressAvailable
                ? details
                : omit(details, 'streetNumber', 'streetName', 'postalCode');
        onSave(detailsToSubmit);
    };

    const handleClickDelete = () => {
        buildingId && dispatch(deleteBuildingRequest(buildingId));
    };

    const handleChangeAddressAvailable = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAddressAvailable(e.target.checked);
    };

    const handleChangeCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
        // Reset the region and city fields if the country changes
        const country = e.currentTarget.value;
        if (details.country !== country) {
            setDetails(address => ({
                ...address,
                country,
                region: '',
                city: '',
                latitude: '',
                longitude: '',
                timeZone: '',
            }));
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.currentTarget;
        let { value } = e.currentTarget;
        const booleanPropertyNames: Record<string, true> = {
            hasBroadcast: true,
        };
        if (name === 'urlSlug') {
            value = toUrlSlug(value, true);
        }
        setDetails(address => ({
            ...address,
            [name]: booleanPropertyNames[name] ? checked : value,
        }));
    };

    const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
        const { name } = e.currentTarget;
        let { value } = e.currentTarget;
        if (name === 'urlSlug') {
            value = toUrlSlug(value);
        }
        setDetails(address => ({
            ...address,
            [name]: value,
        }));
    };

    const handleChangeProperties = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.currentTarget;
        setDetails(details => ({
            ...details,
            properties: {
                ...(details.properties || {}),
                [name as keyof BuildingDetails['properties']]: checked ? true : undefined,
            },
        }));
    };

    const handleChangeWithCoordsReset = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setDetails(details => ({
            ...details,
            latitude: '',
            longitude: '',
            timeZone: '',
            [name]: value,
        }));
    };

    useEffect(() => {
        // Update any fields that were automatically generated on update
        setDetails(address => ({
            ...(building || address),
            longitude: building ? `${building.longitude}` : address.longitude,
            latitude: building ? `${building.latitude}` : address.latitude,
        }));
    }, [ create, building ]);
    

    const parts =
        [ details.streetNumber, details.streetName ]
            .filter(_ => _)
            .map(_ => _!.trim());

    const defaultDisplayName = parts.join(' ');
    const defaultUrlSlug = toUrlSlug(details.displayName || defaultDisplayName);
    const isNorthAmerica = details.country === 'US' || details.country === 'CA';
    const canEditUrlSlug = hasPermission(null, Permission.Admin);

    if (!create && !building) {
        return null;
    }
    return (
        <form
            id="building-form"
            onSubmit={ handleSubmit }
            className={ `ui form${errorMessage ? ' error' : ''}${busy ? ' loading' : ''}` }
        >
            <div className="ui grid">
                <div className="six wide column">
                    <TextField
                        id="building-form_display-name"
                        name="displayName"
                        autoComplete="off"
                        maxLength={ 64 }
                        labelKey="CREATE_BUILDING.FIELD.DISPLAYNAME"
                        placeholder={ defaultDisplayName }
                        value={ details.displayName || '' }
                        onChange={ handleChange }
                    />
                </div>
                { !create &&
                    <div className="ten wide column">
                        <UrlField
                            id="building-form_slug"
                            name="urlSlug"
                            autoComplete="off"
                            maxLength={ 64 }
                            labelKey="CREATE_BUILDING.FIELD.URLSLUG"
                            baseUrl={ `${BASE_URL}/buildings/` }
                            placeholder={ defaultUrlSlug }
                            value={ details.urlSlug || '' }
                            onChange={ handleChange }
                            onBlur={ handleBlur }
                            disabled={ !canEditUrlSlug }
                        />
                    </div>
                }
            </div>

            <div className="ui grid">
                <div className="five wide column">
                    <LegacySelectField
                        id="building-form_country"
                        labelKey="CREATE_BUILDING.FIELD.COUNTRY"
                        required={ true }
                        input={ {
                            /* KILL: remove the input attribute once redux-form has been fully eliminated from the codebase */
                            name: "country",
                            value: details.country || '',
                            onChange: handleChangeCountry,
                        } }
                    >
                        <option value=""/>
                        { sortedCountries.map(({ countryCode, name }) =>
                            <option
                                key={ countryCode }
                                value={ countryCode }
                            >
                                { name }
                            </option>
                        ) }
                    </LegacySelectField>
                </div>
                <div className="five wide column">
                    { details.country && regionsByCountry[details.country]
                        ? <LegacySelectField
                            id="building-form_region"
                            labelKey={ `CREATE_BUILDING.FIELD.${details.country === 'CA' ? 'PROVINCE' : 'STATE'}` }
                            required={ true }
                            input={ {
                                /* KILL: remove the input attribute once redux-form has been fully eliminated from the codebase */
                                name: "region",
                                value: details.region || '',
                                onChange: handleChangeWithCoordsReset,
                            } }
                        >
                            <option value=""/>
                            { regionsByCountry[details.country].map((r: string) =>
                                <option
                                    key={ r }
                                    value={ r }
                                >
                                    { r }
                                </option>
                            ) }
                        </LegacySelectField>
                        : <TextField
                            id="building-form_region"
                            name="region"
                            autoComplete="off"
                            labelKey="CREATE_BUILDING.FIELD.REGION"
                            value={ details.region || '' }
                            onChange={ handleChangeWithCoordsReset }
                        />
                    }
                </div>
                <div className="six wide column">
                    <TextField
                        id="building-form_city"
                        name="city"
                        autoComplete="off"
                        maxLength={ 64 }
                        labelKey="CREATE_BUILDING.FIELD.CITY"
                        required={ true }
                        value={ details.city || '' }
                        onChange={ handleChangeWithCoordsReset }
                    />
                </div>
            </div>

            <div className="ui grid">
                <div className="sixteen wide column">
                    <div className="ui checkbox field check-field">
                        <input
                            id="building-form_address-available"
                            style={ { marginRight: '.5em' } }
                            type="checkbox"
                            checked={ addressAvailable }
                            onChange={ handleChangeAddressAvailable }
                        />
                        <label htmlFor="building-form_address-available">
                            { t('CREATE_BUILDING.FIELD.ADDRESS_AVAILABLE') }
                        </label>
                    </div>
                </div>
            </div>

            { addressAvailable &&
                <div className="ui grid">
                    <div className="three wide column">
                        <TextField
                            id="building-form_number"
                            name="streetNumber"
                            autoComplete="off"
                            maxLength={ 16 }
                            labelKey="CREATE_BUILDING.FIELD.STREETNUMBER"
                            required={ true }
                            value={ details.streetNumber || '' }
                            onChange={ handleChangeWithCoordsReset }
                        />
                    </div>
                    <div className="seven wide column">
                        <TextField
                            id="building-form_name"
                            name="streetName"
                            autoComplete="off"
                            maxLength={ 64 }
                            labelKey="CREATE_BUILDING.FIELD.STREETNAME"
                            required={ true }
                            value={ details.streetName || '' }
                            onChange={ handleChangeWithCoordsReset }
                        />
                    </div>
                    <div className="six wide column">
                        <TextField
                            id="building-form_postalcode"
                            name="postalCode"
                            autoComplete="off"
                            maxLength={ 16 }
                            labelKey="CREATE_BUILDING.FIELD.POSTALCODE"
                            value={ details.postalCode || '' }
                            onChange={ handleChangeWithCoordsReset }
                        />
                    </div>
                </div>
            }

            { !isNew &&
                <div className="ui grid _section-break">
                    <div className="five wide column">
                        <TextField
                            id="building-form_latitude"
                            name="latitude"
                            autoComplete="off"
                            labelKey="CREATE_BUILDING.FIELD.LATITUDE"
                            value={ details.latitude || '' }
                            onChange={ handleChange }
                        />
                    </div>
                    <div className="five wide column">
                        <TextField
                            id="building-form_longitude"
                            name="longitude"
                            autoComplete="off"
                            labelKey="CREATE_BUILDING.FIELD.LONGITUDE"
                            value={ details.longitude || '' }
                            onChange={ handleChange }
                        />
                    </div>
                    <div className="six wide column">
                        <TextField
                            id="building-form_timezone"
                            name="timeZone"
                            labelKey="CREATE_BUILDING.FIELD.TIMEZONE"
                            disabled={ true }
                            readOnly={ true }
                            value={ details.timeZone || '' }
                        />
                    </div>
                </div>
            }

            { hasPermission(buildingId, Permission.EditBuildingFeatures) &&
                <div className="ui grid _section-break">
                    <div className="eight wide column">
                        <CheckField
                            id="building-form_has-broadcast"
                            labelKey="CREATE_BUILDING.FIELD.HAS_BROADCAST"
                            input={ {
                                name: "hasBroadcast",
                                checked: !!details.hasBroadcast,
                                onChange: handleChange,
                            } }
                        />
                    </div>
                    <div className="eight wide column">
                        { hasPermission(null, Permission.Admin) &&
                            <CheckField
                                id="building-form_disable-remote-update"
                                labelKey="CREATE_BUILDING.FIELD.DISABLE_REMOTE_UPDATE"
                                input={ {
                                    name: "remoteUpdateDisabled",
                                    checked: !!details.properties.remoteUpdateDisabled,
                                    onChange: handleChangeProperties,
                                } }
                            />
                        }
                        { isNorthAmerica && hasPermission(null, Permission.AllOrganizations) &&
                            <CheckField
                                id="building-form_decline-vertical-city"
                                labelKey="CREATE_BUILDING.FIELD.DECLINE_VERTICAL_CITY"
                                input={ {
                                    name: "declinedVerticalCity",
                                    checked: !!details.properties.declinedVerticalCity,
                                    onChange: handleChangeProperties,
                                } }
                            />
                        }
                    </div>
                </div>
            }

            <div className="_buttons">
                <button
                    className="ui primary submit button"
                    type="submit"
                    disabled={ busy }
                >
                    <div className="content">
                        { t(isNew ? 'COMMAND.CREATE_BUILDING' : 'COMMAND.SAVE') }
                    </div>
                </button>
                { !isNew && hasPermission(buildingId, Permission.DeleteBuilding) &&
                    <button
                        className="ui red basic button _delete-button"
                        type="button"
                        disabled={ busy }
                        onClick={ handleClickDelete }
                    >
                        { t('COMMAND.DELETE') }
                    </button>
                }
            </div>

            <SuccessMessage message={ successMessage }/>
            <ErrorMessage message={ errorMessage }/>
        </form>
    );
};
